'use client';
import React, { useState, useCallback } from 'react';

const topDestinations = [
  {
    name: 'Flights to New York',
    price: '$89',
    image: 'https://images.unsplash.com/photo-1717857491817-f378ae09f6b3',
    alt: 'New York City skyline with iconic skyscrapers and Manhattan bridge',
    saving: '58%',
    iata: 'JFK',
    fromIata: 'LAX',
  },
  {
    name: 'Flights to Miami',
    price: '$112',
    image: 'https://images.unsplash.com/photo-1577834045932-1619c37b4970',
    alt: 'Miami Beach Florida with turquoise ocean water and white sandy beach',
    saving: '52%',
    iata: 'MIA',
    fromIata: 'JFK',
  },
  {
    name: 'Flights to Las Vegas',
    price: '$98',
    image: 'https://images.unsplash.com/photo-1628840803162-6824ada8c7b0',
    alt: 'Las Vegas Nevada Strip at night with bright neon casino lights',
    saving: '50%',
    iata: 'LAS',
    fromIata: 'LAX',
  },
  {
    name: 'Flights to London',
    price: '$349',
    image: 'https://images.unsplash.com/photo-1671890217001-fadd792eb660',
    alt: 'London England Big Ben and Westminster Bridge over River Thames',
    saving: '55%',
    iata: 'LHR',
    fromIata: 'JFK',
  },
  {
    name: 'Flights to Paris',
    price: '$389',
    image: 'https://images.unsplash.com/photo-1665845246332-bcf4b8a03d62',
    alt: 'Paris France Eiffel Tower at sunset with golden sky',
    saving: '54%',
    iata: 'CDG',
    fromIata: 'JFK',
  },
  {
    name: 'Flights to Tokyo',
    price: '$599',
    image: 'https://images.unsplash.com/photo-1593839154339-377e24b3ba32',
    alt: 'Tokyo Japan skyline with Mount Fuji in background at dawn',
    saving: '48%',
    iata: 'NRT',
    fromIata: 'LAX',
  },
  {
    name: 'Flights to Cancun',
    price: '$189',
    image:
      'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&q=80',
    alt: 'Cancun Mexico turquoise Caribbean Sea with luxury resort beach',
    saving: '56%',
    iata: 'CUN',
    fromIata: 'MIA',
  },
  {
    name: 'Flights to Dubai',
    price: '$549',
    image: 'https://images.unsplash.com/photo-1725564053095-1a07b2bc89a3',
    alt: 'Dubai UAE Burj Khalifa skyscraper with modern city skyline at night',
    saving: '51%',
    iata: 'DXB',
    fromIata: 'JFK',
  },
];

const ITEMS_PER_SLIDE = 4;

interface TopDestinationsSectionProps {
  onDestinationClick?: (name: string) => void;
}

export default function TopDestinationsSection({
  onDestinationClick,
}: TopDestinationsSectionProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const totalSlides = Math.ceil(topDestinations.length / ITEMS_PER_SLIDE);

  const next = useCallback(() => setCurrentSlide((c) => (c + 1) % totalSlides), [totalSlides]);
  const prev = useCallback(
    () => setCurrentSlide((c) => (c - 1 + totalSlides) % totalSlides),
    [totalSlides]
  );

  return (
    <div className="bg-white py-20 border-b border-slate-100">
      <div className="max-w-[1170px] mx-auto px-4">
        {/* Section Header with Carousel Navigation */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
          <div className="max-w-xl text-left">
            <div
              style={{ height: '30px' }}
              className="inline-flex items-center gap-1.5 bg-accent/15 border border-accent/30 px-3.5 py-1.2 rounded-full text-[10px] font-black text-accent uppercase tracking-widest mb-4 shadow-sm"
            >
              HANDPICKED PRIVATE FARES
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight leading-none mb-3 uppercase">
              Top Flight Destinations
            </h2>
            <p className="text-slate-500 text-xs md:text-sm font-semibold leading-relaxed">
              Explore real-time private airline contract deals with premium seating and lowest-price
              guarantees.
            </p>
          </div>

          {/* Custom Carousel Controls */}
          <div className="flex gap-3 self-start md:self-end">
            <button
              onClick={prev}
              className="w-11 h-11 border border-slate-200 hover:border-accent hover:bg-slate-950 hover:text-white text-slate-600 flex items-center justify-center rounded-2xl transition-all duration-300 shadow-sm active:scale-95"
              aria-label="Previous"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <button
              onClick={next}
              className="w-11 h-11 border border-slate-200 hover:border-accent hover:bg-slate-950 hover:text-white text-slate-600 flex items-center justify-center rounded-2xl transition-all duration-300 shadow-sm active:scale-95"
              aria-label="Next"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Premium Dest Slider Grid with 3D Staggered Cascade Fan Transitions */}
        <div className="relative w-full min-h-[460px] lg:min-h-[380px]">
          {/* Slide 1 (Destinations 1-4) with staggered 3D cascade */}
          <div
            className={
              currentSlide === 0
                ? 'relative w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 z-10'
                : 'absolute inset-0 w-full pointer-events-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 z-0'
            }
          >
            {topDestinations.slice(0, 4).map((dest, i) => {
              const isActive = currentSlide === 0;
              return (
                <div
                  key={`dest-slide1-${i}`}
                  onClick={() => onDestinationClick?.(dest.name)}
                  className="group bg-white rounded-3xl border border-slate-200 hover:border-accent/40 shadow-[0_12px_35px_rgba(0,0,0,0.05)] hover:shadow-[0_22px_45px_rgba(0,0,0,0.12)] cursor-pointer flex flex-col relative overflow-hidden transition-all duration-500 hover:-translate-y-1.5"
                  style={{
                    transitionProperty: 'transform, opacity, filter',
                    transitionDuration: '800ms',
                    transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${i * 60}ms`,
                    transform: isActive
                      ? 'scale(1) translateY(0) rotate(0deg)'
                      : 'scale(0.88) translateY(60px) rotate(-4deg)',
                    opacity: isActive ? 1 : 0,
                    filter: isActive ? 'blur(0px)' : 'blur(4px)',
                  }}
                >
                  {/* Photo Area */}
                  <div className="relative overflow-hidden h-48 w-full">
                    <img
                      src={dest.image}
                      alt={dest.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                    />

                    {/* Modern Dark Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/10 to-transparent group-hover:via-slate-950/40 transition-all duration-500"></div>

                    {/* Top-Left Private Deal Tag */}
                    <div className="absolute top-4 left-4 bg-slate-950/60 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10 flex items-center gap-1 shadow-md">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                      <span className="text-[8px] font-black text-sky-300 uppercase tracking-widest">
                        PRIVATE DEAL
                      </span>
                    </div>

                    {/* Top-Right Saving Indicator */}
                    <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[9px] font-black px-2.5 py-1 rounded-full shadow-md">
                      -{dest.saving}
                    </div>

                    {/* Floating IATA Code Badge above title */}
                    <div className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-sm px-2.5 py-0.8 rounded-lg border border-white/10 text-[9px] font-black text-white uppercase tracking-wider">
                      {dest.iata} DIRECT
                    </div>
                  </div>

                  {/* Ticket Stub Dashed Divider */}
                  <div className="relative w-full h-px bg-white group-hover:bg-slate-50 transition-colors duration-300">
                    <div className="absolute top-0 left-0 right-0 border-t border-dashed border-slate-300" />
                    {/* Left circular ticket punch-out hole */}
                    <div className="absolute -left-[9px] -top-[8px] w-4 h-4 rounded-full bg-slate-50 border border-slate-200 shadow-[inset_-3px_0_5px_rgba(0,0,0,0.08)] z-20 group-hover:bg-slate-50 transition-colors" />
                    {/* Right circular ticket punch-out hole */}
                    <div className="absolute -right-[9px] -top-[8px] w-4 h-4 rounded-full bg-slate-50 border border-slate-200 shadow-[inset_3px_0_5px_rgba(0,0,0,0.08)] z-20 group-hover:bg-slate-50 transition-colors" />
                  </div>

                  {/* Luxury Boarding Card Info Footer */}
                  <div className="px-6 pt-5 pb-7 flex items-start justify-between bg-white group-hover:bg-slate-50 border-t border-slate-200/50 transition-colors duration-300 rounded-b-3xl">
                    {/* Left Column: Route, City Name, Connecting path */}
                    <div className="text-left">
                      <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">
                        ROUTE
                      </span>
                      <span className="font-extrabold text-slate-800 text-[16px] mt-0.5 block group-hover:text-slate-950 transition-colors leading-none">
                        {dest.name.replace('Flights to ', '')}
                      </span>
                      {/* High-Fidelity Flight Pathway Details */}
                      <div className="flex items-center gap-1 text-[10px] text-slate-400 font-extrabold mt-2 leading-none uppercase">
                        <span>{dest.fromIata}</span>
                        <svg
                          className="text-slate-300 group-hover:text-accent transition-colors"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path d="M5 12h14" />
                          <path d="m12 5 7 7-7 7" />
                        </svg>
                        <span className="text-slate-500 font-black">{dest.iata}</span>
                      </div>
                    </div>

                    {/* Right Column: Pricing & Fees Subtitles */}
                    <div className="text-right">
                      <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">
                        ONE WAY FROM
                      </span>
                      <span className="font-black text-accent text-[20px] group-hover:scale-105 transition-all mt-0.5 block leading-none">
                        {dest.price}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Slide 2 (Destinations 5-8) with staggered 3D cascade */}
          <div
            className={
              currentSlide === 1
                ? 'relative w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 z-10'
                : 'absolute inset-0 w-full pointer-events-none grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 z-0'
            }
          >
            {topDestinations.slice(4, 8).map((dest, i) => {
              const isActive = currentSlide === 1;
              return (
                <div
                  key={`dest-slide2-${i}`}
                  onClick={() => onDestinationClick?.(dest.name)}
                  className="group bg-white rounded-3xl border border-slate-200 hover:border-accent/40 shadow-[0_12px_35px_rgba(0,0,0,0.05)] hover:shadow-[0_22px_45px_rgba(0,0,0,0.12)] cursor-pointer flex flex-col relative overflow-hidden transition-all duration-500 hover:-translate-y-1.5"
                  style={{
                    transitionProperty: 'transform, opacity, filter',
                    transitionDuration: '800ms',
                    transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transitionDelay: `${i * 60}ms`,
                    transform: isActive
                      ? 'scale(1) translateY(0) rotate(0deg)'
                      : 'scale(0.88) translateY(60px) rotate(4deg)',
                    opacity: isActive ? 1 : 0,
                    filter: isActive ? 'blur(0px)' : 'blur(4px)',
                  }}
                >
                  {/* Photo Area */}
                  <div className="relative overflow-hidden h-48 w-full">
                    <img
                      src={dest.image}
                      alt={dest.alt}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
                    />

                    {/* Modern Dark Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-900/10 to-transparent group-hover:via-slate-950/40 transition-all duration-500"></div>

                    {/* Top-Left Private Deal Tag */}
                    <div className="absolute top-4 left-4 bg-slate-950/60 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10 flex items-center gap-1 shadow-md">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse"></span>
                      <span className="text-[8px] font-black text-sky-300 uppercase tracking-widest">
                        PRIVATE DEAL
                      </span>
                    </div>

                    {/* Top-Right Saving Indicator */}
                    <div className="absolute top-4 right-4 bg-emerald-500 text-white text-[9px] font-black px-2.5 py-1 rounded-full shadow-md">
                      -{dest.saving}
                    </div>

                    {/* Floating IATA Code Badge above title */}
                    <div className="absolute bottom-4 left-4 bg-white/20 backdrop-blur-sm px-2.5 py-0.8 rounded-lg border border-white/10 text-[9px] font-black text-white uppercase tracking-wider">
                      {dest.iata} DIRECT
                    </div>
                  </div>

                  {/* Ticket Stub Dashed Divider */}
                  <div className="relative w-full h-px bg-white group-hover:bg-slate-50 transition-colors duration-300">
                    <div className="absolute top-0 left-0 right-0 border-t border-dashed border-slate-300" />
                    {/* Left circular ticket punch-out hole */}
                    <div className="absolute -left-[9px] -top-[8px] w-4 h-4 rounded-full bg-slate-50 border border-slate-200 shadow-[inset_-3px_0_5px_rgba(0,0,0,0.08)] z-20 group-hover:bg-slate-50 transition-colors" />
                    {/* Right circular ticket punch-out hole */}
                    <div className="absolute -right-[9px] -top-[8px] w-4 h-4 rounded-full bg-slate-50 border border-slate-200 shadow-[inset_3px_0_5px_rgba(0,0,0,0.08)] z-20 group-hover:bg-slate-50 transition-colors" />
                  </div>

                  {/* Luxury Boarding Card Info Footer */}
                  <div className="px-6 pt-5 pb-7 flex items-start justify-between bg-white group-hover:bg-slate-50 border-t border-slate-200/50 transition-colors duration-300 rounded-b-3xl">
                    {/* Left Column: Route, City Name, Connecting path */}
                    <div className="text-left">
                      <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">
                        ROUTE
                      </span>
                      <span className="font-extrabold text-slate-800 text-[16px] mt-0.5 block group-hover:text-slate-950 transition-colors leading-none">
                        {dest.name.replace('Flights to ', '')}
                      </span>
                      {/* High-Fidelity Flight Pathway Details */}
                      <div className="flex items-center gap-1 text-[10px] text-slate-400 font-extrabold mt-2 leading-none uppercase">
                        <span>{dest.fromIata}</span>
                        <svg
                          className="text-slate-300 group-hover:text-accent transition-colors"
                          width="10"
                          height="10"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path d="M5 12h14" />
                          <path d="m12 5 7 7-7 7" />
                        </svg>
                        <span className="text-slate-500 font-black">{dest.iata}</span>
                      </div>
                    </div>

                    {/* Right Column: Pricing & Fees Subtitles */}
                    <div className="text-right">
                      <span className="text-[9px] text-slate-400 font-extrabold uppercase tracking-wider block">
                        ONE WAY FROM
                      </span>
                      <span className="font-black text-accent text-[20px] group-hover:scale-105 transition-all mt-0.5 block leading-none">
                        {dest.price}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
